// NavRotas.js — Atalhos de navegação da faixa inferior do header, com um
// marcador em losango que desliza até a rota atual.
//
// Inspirado no "Anime Navbar" (21st.dev/@jatin-yadav05): lá um mascote fica
// acima do item ativo e um losango pendurado embaixo dele aponta para o item;
// o conjunto todo escorrega de um item para o outro com mola (`layoutId`) e
// flutua de leve. Aqui fica SÓ o losango, no laranja da marca — o mascote
// não tem nada a ver com uma editora acadêmica.
//
// Duas diferenças de implementação, ambas de propósito:
//
//  1. O deslize é CSS (`transform: translateX`), não `layoutId` do motion.
//     `layoutId` exige as features de layout animation, que NÃO estão no
//     `domAnimation` que o site carrega — usá-lo obrigaria a trocar para o
//     pacote completo e engordar o bundle por causa de um losango.
//
//  2. A posição é medida do DOM e reaplicada por ResizeObserver. Sem isso o
//     marcador nasce no lugar errado: a fonte Roboto carrega depois do
//     primeiro render e muda a largura dos rótulos.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

// Geometria medida da barra real (md+): a faixa tem 56 px, o rótulo ocupa 16 px
// centrados, então sobram exatamente 20 px acima dele. Um quadrado de 9 px
// girado 45° ocupa ~12,7 px de altura visual — cabe nesses 20 px com folga dos
// dois lados. Com 10 px e o recuo anterior, o losango encostava no texto.
const LADO = 9;
/** Distância do topo do rótulo até o topo da caixa do losango. */
const RECUO_TOPO = 16;

const NavRotas = ({ links, onNavigate }) => {
  const location = useLocation();
  const navRef = useRef(null);
  const itemRefs = useRef({});

  const [marcador, setMarcador] = useState(null); // { x } em px, ou null
  const [algumHover, setAlgumHover] = useState(false);

  // Qual link corresponde à rota atual. Links externos (WhatsApp) nunca ficam
  // ativos: a pessoa não "está" neles.
  const rotuloAtivo = (() => {
    const atual = location.pathname.replace(/\/+$/, "");
    let escolhido = null;
    links.forEach((l) => {
      if (l.external) return;
      const alvo = l.to.replace(/\/+$/, "");
      // `exact` existe por causa do INÍCIO: a home é prefixo de todas as
      // outras rotas, então sem isso ela casaria em qualquer página.
      const casa = l.exact ? atual === alvo : atual === alvo || atual.startsWith(alvo + "/");
      if (casa) {
        // Em caso de empate, vence a rota mais específica.
        if (!escolhido || alvo.length > escolhido.alvo.length) {
          escolhido = { rotulo: l.label, alvo };
        }
      }
    });
    return escolhido?.rotulo || null;
  })();

  const medir = useCallback(() => {
    const nav = navRef.current;
    const item = rotuloAtivo ? itemRefs.current[rotuloAtivo] : null;
    if (!nav || !item) {
      setMarcador(null);
      return;
    }
    const r = nav.getBoundingClientRect();
    const ri = item.getBoundingClientRect();
    setMarcador({ x: ri.left - r.left + ri.width / 2 });
  }, [rotuloAtivo]);

  // useLayoutEffect: posiciona antes da pintura, senão o marcador aparece no
  // canto e escorrega até o lugar certo na primeira renderização.
  useLayoutEffect(() => { medir(); }, [medir]);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const observador = new ResizeObserver(medir);
    observador.observe(nav);
    Object.values(itemRefs.current).forEach((el) => el && observador.observe(el));

    window.addEventListener("resize", medir);
    // A fonte chega depois do primeiro render e muda a largura dos rótulos.
    if (document.fonts?.ready) document.fonts.ready.then(medir).catch(() => {});

    return () => {
      observador.disconnect();
      window.removeEventListener("resize", medir);
    };
  }, [medir]);

  return (
    <nav
      ref={navRef}
      onMouseLeave={() => setAlgumHover(false)}
      className="hidden md:flex items-center gap-6 lg:gap-8 flex-1 justify-center relative"
    >
      {links.map((l) => {
        const ativo = l.label === rotuloAtivo;
        // `link-sublinhado`: traço que cresce do centro no hover, na cor do
        // próprio link (currentColor). Ver o bloco no App.css.
        const comum =
          "link-sublinhado transition-colors " +
          (ativo ? "text-insigne-orange" : "hover:text-insigne-orange");

        return l.external ? (
          <a
            key={l.label}
            ref={(el) => { itemRefs.current[l.label] = el; }}
            href={l.to}
            target="_blank"
            rel="noreferrer"
            onMouseEnter={() => setAlgumHover(true)}
            className={comum}
          >
            {l.label}
          </a>
        ) : (
          <Link
            key={l.label}
            ref={(el) => { itemRefs.current[l.label] = el; }}
            to={l.to}
            // `aoClicar`: alguns destinos são ABAS de outra página (a Loja de
            // Créditos é uma aba do perfil, e o perfil não põe a aba na URL).
            // O Link leva à rota; o callback diz qual aba abrir.
            onClick={() => { if (onNavigate) onNavigate(); if (l.aoClicar) l.aoClicar(); }}
            onMouseEnter={() => setAlgumHover(true)}
            aria-current={ativo ? "page" : undefined}
            className={comum}
          >
            {l.label}
          </Link>
        );
      })}

      {/* ===== Marcador em losango =====
          Fica ACIMA do rótulo, como no componente de origem: lá ele é o
          rabicho do balão do mascote, apontando para baixo, para o item ativo.

          Dois elementos aninhados porque as duas animações disputam a mesma
          propriedade `transform`: o de fora cuida do deslize horizontal
          (transição), o de dentro cuida do flutuar e da rotação de 45°
          (keyframes). Num elemento só, uma sobrescreveria a outra. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-0 transition-[transform,opacity] duration-[420ms]"
        style={{
          top: -RECUO_TOPO,
          // cubic-bezier com leve ultrapassagem = a mola do componente original
          transitionTimingFunction: "cubic-bezier(0.34, 1.4, 0.64, 1)",
          transform: `translateX(${(marcador?.x ?? 0) - LADO / 2}px)`,
          opacity: marcador ? 1 : 0,
        }}
      >
        <span
          className={
            "block bg-insigne-orange rounded-[2px] " +
            (algumHover ? "marcador-nav-vivo" : "marcador-nav-parado")
          }
          style={{
            width: LADO,
            height: LADO,
            boxShadow: "0 0 10px rgb(var(--c-orange) / 0.9), 0 0 22px rgb(var(--c-orange) / 0.45)",
          }}
        />
      </span>
    </nav>
  );
};

export default NavRotas;
